import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/UI/PageTransition';
import {
  FaFutbol, FaTrophy, FaStar, FaBolt, FaFire,
  FaSignOutAlt, FaEdit, FaChartBar, FaHistory, FaUserPlus, FaCheck, FaTimes, FaCamera, FaKey, FaQuestionCircle,
  FaUsers, FaSearch, FaUserFriends, FaGift, FaCopy, FaShareAlt, FaCrosshairs
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Avatar from '../components/UI/Avatar';
import LevelBadge from '../components/UI/LevelBadge';
import Button from '../components/UI/Button';
import ReferralSection from '../components/UI/ReferralSection';
import FingerprintSection from '../components/UI/FingerprintSection';
import BiometricSection from '../components/UI/BiometricSection';
import BadgeShield, { BADGE_META } from '../components/UI/BadgeShield';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import usePushNotification from '../hooks/usePushNotification';
import { FaBell, FaBellSlash } from 'react-icons/fa';
import { BUILD_ID } from '../buildInfo';


function Profile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: authUser, logout, updateUser } = useAuth();

  const isOwnProfile = id === 'me' || String(id) === String(authUser?.id);
  const { permission, subscribed, subscribe: subscribePush, unsubscribe: unsubscribePush, isSupported: pushSupported } = usePushNotification();

  const DEMO_HISTORY = [
    { _id: 'g1', mode: 'Solo', score: 175, correct: 7, total: 10, date: new Date(Date.now() - 3600000) },
    { _id: 'g2', mode: 'Duel', score: 100, correct: 4, total: 5, date: new Date(Date.now() - 86400000), won: true },
    { _id: 'g3', mode: 'Solo', score: 125, correct: 5, total: 10, date: new Date(Date.now() - 172800000) },
  ];

  const [profileUser, setProfileUser] = useState(isOwnProfile ? authUser : null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(!isOwnProfile);
  const [editPseudo, setEditPseudo] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [newPseudo, setNewPseudo] = useState(profileUser?.pseudo || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [prestiging, setPrestiging] = useState(false);
  const fileInputRef = useRef(null);

  // Parrainage
  const [referral, setReferral] = useState(null);

  // Amis
  const [friends, setFriends] = useState([]);
  const [addFriendPseudo, setAddFriendPseudo] = useState('');
  const [sendingFriendReq, setSendingFriendReq] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState(null); // 'none'|'pending'|'accepted'
  const [friendshipDirection, setFriendshipDirection] = useState(null); // 'sent'|'received'
  const [challenging, setChallenging] = useState(null); // friendId en cours de défi
  const [profileBadges, setProfileBadges] = useState([]);
  const [profileStats, setProfileStats]   = useState(null);

  useEffect(() => {
    if (!isOwnProfile) {
      api.get(`/profile/${id}`)
        .then(({ data }) => {
          setProfileUser(data.user || data);
          if (data.stats)   setProfileStats(data.stats);
          if (data.history) setHistory(data.history);
          if (data.badges)  setProfileBadges(data.badges);
        })
        .catch(() => toast.error('Profil introuvable'))
        .finally(() => setLoading(false));
      // Vérifie l'état de la relation avec cet utilisateur (bidirectionnel)
      api.get(`/profile/friends/status/${id}`)
        .then(({ data }) => {
          setFriendshipStatus(data.status || 'none');
          setFriendshipDirection(data.direction || null);
        })
        .catch(() => setFriendshipStatus('none'));
    }
  }, [id, isOwnProfile]);

  useEffect(() => {
    if (isOwnProfile) {
      api.get('/profile/history?limit=5')
        .then(({ data }) => setHistory(data.history || data.games || data || []))
        .catch(() => setHistory(DEMO_HISTORY));
      // Charger les badges du joueur connecté
      if (authUser?.id) {
        api.get(`/profile/${authUser.id}`)
          .then(({ data }) => {
            if (data.badges) setProfileBadges(data.badges);
            if (data.stats)  setProfileStats(data.stats);
          })
          .catch(() => {});
      }
    }
  }, [isOwnProfile, authUser?.id]);

  useEffect(() => {
    if (isOwnProfile) {
      api.get('/profile/friends/pending')
        .then(({ data }) => setPendingRequests(data.requests || []))
        .catch(() => {});
      api.get('/profile/friends')
        .then(({ data }) => setFriends(data.friends || []))
        .catch(() => {});
      if (authUser?.plan === 'premium') {
        api.get('/referral/me')
          .then(({ data }) => setReferral(data))
          .catch(() => {});
      }
    }
  }, [isOwnProfile]);

  const displayHistory = history.length > 0 ? history : DEMO_HISTORY;

  // Statut du changement de pseudo : max 3 changements, 7 jours entre deux —
  // calculé côté client pour prévenir avant l'essai (le serveur reste la
  // source de vérité et refusera de toute façon si la fenêtre a changé).
  const pseudoChangeCount = authUser?.pseudo_change_count ?? 0;
  const pseudoChangesLeft = Math.max(0, 3 - pseudoChangeCount);
  const pseudoCooldownDays = (() => {
    if (!authUser?.pseudo_changed_at) return 0;
    const daysSince = (Date.now() - new Date(authUser.pseudo_changed_at).getTime()) / 86_400_000;
    return daysSince < 7 ? Math.ceil(7 - daysSince) : 0;
  })();
  const canChangePseudo = pseudoChangesLeft > 0 && pseudoCooldownDays === 0;
  const pseudoStatusMessage = pseudoChangesLeft === 0
    ? 'Limite de 3 changements atteinte'
    : pseudoCooldownDays > 0
      ? `Prochain changement possible dans ${pseudoCooldownDays} jour(s)`
      : `${pseudoChangesLeft} changement(s) restant(s)`;

  const handleEditPseudoClick = () => {
    if (!canChangePseudo) {
      toast.error(pseudoStatusMessage);
      return;
    }
    setNewPseudo(authUser?.pseudo || '');
    setEditPseudo(true);
  };

  const handlePrestige = async () => {
    if (!window.confirm('Prestiger remet ton niveau à 1, mais te fait gagner un badge Prestige permanent — ton XP total n\'est jamais perdu. Confirmer ?')) return;
    setPrestiging(true);
    try {
      const { data } = await api.post('/profile/prestige');
      updateUser({ ...authUser, ...data.user });
      setProfileUser((u) => ({ ...u, ...data.user }));
      toast.success(`Prestige ${data.user.prestige_level} débloqué ! 🌟`, { duration: 5000 });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors du prestige');
    } finally {
      setPrestiging(false);
    }
  };

  const handleSavePseudo = async () => {
    if (newPseudo.length < 3) { toast.error('Pseudo trop court'); return; }
    try {
      const { data } = await api.put('/profile', { pseudo: newPseudo });
      updateUser({ ...authUser, ...(data.user || { pseudo: newPseudo }) });
      setProfileUser((u) => ({ ...u, pseudo: newPseudo }));
      setEditPseudo(false);
      toast.success('Pseudo mis à jour !');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur de mise à jour');
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image trop lourde (max 2 Mo)'); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const { data } = await api.put('/profile', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateUser({ ...authUser, ...data.user });
      setProfileUser((u) => ({ ...u, avatar_url: data.user.avatar_url }));
      toast.success('Photo mise à jour !');
    } catch {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (pwForm.next.length < 8) { toast.error('Minimum 8 caractères'); return; }
    setSavingPw(true);
    try {
      await api.put('/profile/password', { currentPassword: pwForm.current, newPassword: pwForm.next });
      toast.success('Mot de passe modifié !');
      setShowPasswordForm(false);
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSavingPw(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnecté');
    navigate('/login');
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword]   = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error('Saisis ton mot de passe'); return; }
    setDeletingAccount(true);
    try {
      await api.delete('/profile/me', { data: { password: deletePassword } });
      logout();
      toast.success('Compte supprimé définitivement');
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleAcceptFriend = async (userId) => {
    try {
      await api.post(`/profile/friends/accept/${userId}`);
      // Retire des demandes en attente
      const accepted = pendingRequests.find(r => r.user_id === userId);
      setPendingRequests(prev => prev.filter(r => r.user_id !== userId));
      // Ajoute immédiatement à la liste d'amis sans recharger
      if (accepted) {
        setFriends(prev => [...prev, {
          id: accepted.user_id,
          pseudo: accepted.pseudo,
          avatar_url: accepted.avatar_url,
          level: accepted.level,
        }]);
      }
      toast.success('Ami accepté !');
    } catch {
      toast.error('Erreur lors de l\'acceptation');
    }
  };

  const handleRejectFriend = async (userId) => {
    try {
      await api.post(`/profile/friends/reject/${userId}`);
      setPendingRequests(prev => prev.filter(r => r.user_id !== userId));
      toast('Demande refusée', { icon: '👋' });
    } catch {
      toast.error('Erreur lors du rejet');
    }
  };

  // Envoyer une demande d'ami par pseudo (propre profil)
  const handleSendFriendRequest = async (e) => {
    e.preventDefault();
    if (!addFriendPseudo.trim()) return;
    setSendingFriendReq(true);
    try {
      await api.post('/profile/friends/add', { pseudo: addFriendPseudo.trim() });
      toast.success(`Demande envoyée à ${addFriendPseudo.trim()} !`);
      setAddFriendPseudo('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur envoi de demande');
    } finally {
      setSendingFriendReq(false);
    }
  };

  // Ajouter l'utilisateur du profil public comme ami
  const handleAddThisUser = async () => {
    setSendingFriendReq(true);
    try {
      await api.post('/profile/friends/add', { friendId: id });
      setFriendshipStatus('pending');
      toast.success('Demande d\'ami envoyée !');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSendingFriendReq(false);
    }
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
      </div>
    );
  }

  const user = profileUser || authUser;
  const niveau = user?.niveau || user?.level || 1;
  const prestigeLevel = user?.prestige_level || 0;
  const xp = user?.xp || 0;
  // Courbe progressive (29/10/2026) : passer du niveau L à L+1 coûte 500*L XP
  // (plus c'est haut, plus c'est long), donc la XP cumulée pour avoir atteint
  // le niveau L vaut cumulative(L) = 250*L*(L-1) — même formule que côté
  // serveur (server/src/utils/scoring.js), à garder synchronisée. Le système
  // de Prestige décale ce calcul de PRESTIGE_XP_THRESHOLD (= cumulative(150))
  // par cycle déjà accompli, sans jamais tronquer le total d'XP réel.
  const LEVEL_CAP = 150;
  const cumulativeXpForLevel = (lvl) => 250 * lvl * (lvl - 1);
  const PRESTIGE_XP_THRESHOLD = cumulativeXpForLevel(LEVEL_CAP); // 5 587 500
  const cycleBase = prestigeLevel * PRESTIGE_XP_THRESHOLD;
  const xpBase = cycleBase + cumulativeXpForLevel(niveau);
  // Au niveau max (150), pas de progression suivante — le bouton Prestige prend le relais
  const xpForNext = niveau >= LEVEL_CAP ? xpBase : cycleBase + cumulativeXpForLevel(niveau + 1);
  const xpInLevel = xp - xpBase;
  const xpNeeded = xpForNext - xpBase;
  const xpPercent = Math.min(Math.max((xpInLevel / xpNeeded) * 100, 0), 100);
  const canPrestige = isOwnProfile && niveau >= LEVEL_CAP;
  // Niveau "hérité" : le plancher (cf. migration 009) affiche un niveau que la
  // XP réelle, sous la nouvelle courbe, n'a pas encore atteint. Sans ce test,
  // la barre afficherait un XP manquant énorme et trompeur (ex. "66 000 XP
  // pour le niveau suivant" alors que le joueur a bien plus de XP que ça).
  const isLegacyLevel = xp < xpBase;
  // Stats depuis l'API (profileStats) — toujours fiable
  const stats = {
    total_games:  parseInt(profileStats?.total_games  ?? 0),
    solo_games:   parseInt(profileStats?.solo_games   ?? 0),
    solo_wins:    parseInt(profileStats?.solo_wins    ?? 0),
    solo_losses:  parseInt(profileStats?.solo_losses  ?? 0),
    duel_games:   parseInt(profileStats?.duel_games   ?? 0),
    duel_wins:    parseInt(profileStats?.duel_wins    ?? 0),
    duel_losses:  parseInt(profileStats?.duel_losses  ?? 0),
    avg_score:    parseInt(profileStats?.avg_score    ?? 0),
    best_score:   parseInt(profileStats?.best_score   ?? 0),
  };
  return (
    <PageTransition variant="slideUp">
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '80px' }}>
      <Navbar showBack title="PROFIL" showCoins={false} />

      {/* En-tête profil */}
      <motion.div
        className="flex flex-col items-center py-6 px-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Avatar avec bouton upload */}
        <div className="relative mb-4">
          <Avatar user={user} size="2xl" showBorder />
          {isOwnProfile && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarChange}
              />
              <button
                className="absolute bottom-0 right-0 w-8 h-8 bg-qfl-gold rounded-full flex items-center justify-center shadow-lg disabled:opacity-60"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                title="Changer la photo de profil"
              >
                {uploadingAvatar
                  ? <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  : <FaCamera size={11} className="text-black" />}
              </button>
            </>
          )}
        </div>

        {/* Pseudo */}
        {editPseudo ? (
          <div className="mb-2">
            <div className="flex items-center gap-2">
              <input
                value={newPseudo}
                onChange={(e) => setNewPseudo(e.target.value)}
                className="form-input text-center text-lg font-bold py-2"
                maxLength={20}
                autoFocus
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              />
              <Button variant="green" size="sm" onClick={handleSavePseudo}>OK</Button>
              <Button variant="ghost" size="sm" onClick={() => setEditPseudo(false)}>✕</Button>
            </div>
            <p className="text-white/40 text-xs text-center mt-1.5">{pseudoStatusMessage}</p>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-1">
            <h2
              className="text-2xl font-black text-white"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              {user?.pseudo || 'Joueur'}
            </h2>
            {isOwnProfile && (
              <button
                onClick={handleEditPseudoClick}
                title={pseudoStatusMessage}
                className="text-white/30 hover:text-white/70 transition-colors"
              >
                <FaEdit size={12} />
              </button>
            )}
          </div>
        )}

        <LevelBadge level={niveau} prestige={user?.prestige_level || 0} size="md" className="mb-3" />

        {canPrestige && (
          <button
            onClick={handlePrestige}
            disabled={prestiging}
            className="mb-3 px-4 py-2 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg,#f0b429,#c97d08)',
              color: '#0d2b1a',
              fontFamily: "'Rajdhani', sans-serif",
              boxShadow: '0 0 14px rgba(240,180,41,0.5)',
            }}
          >
            {prestiging
              ? <div className="w-3.5 h-3.5 border-2 border-qfl-dark border-t-transparent rounded-full animate-spin" />
              : '🌟'}
            Prestiger
          </button>
        )}

        {/* Bouton ami — profil public */}
        {!isOwnProfile && authUser && (
          <div className="mb-3">
            {friendshipStatus === 'accepted' ? (
              <span className="flex items-center gap-2 text-sm text-green-400 font-bold px-4 py-2 rounded-full"
                style={{ background: 'rgba(34,197,94,0.1)', fontFamily: "'Rajdhani', sans-serif" }}>
                <FaCheck size={12} /> AMI
              </span>
            ) : friendshipStatus === 'pending' && friendshipDirection === 'received' ? (
              /* L'autre a envoyé une demande → on peut accepter ou refuser depuis ici */
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/profile/friends/accept/${id}`);
                      setFriendshipStatus('accepted');
                      setFriendshipDirection(null);
                      toast.success('Ami accepté !');
                    } catch { toast.error('Erreur'); }
                  }}
                  className="flex items-center gap-1.5 text-sm font-black px-4 py-2 rounded-full"
                  style={{ background: 'rgba(34,197,94,0.2)', color: '#22c55e', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  <FaCheck size={11} /> ACCEPTER
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/profile/friends/reject/${id}`);
                      setFriendshipStatus('none');
                      setFriendshipDirection(null);
                      toast('Demande refusée', { icon: '👋' });
                    } catch { toast.error('Erreur'); }
                  }}
                  className="flex items-center gap-1.5 text-sm font-black px-4 py-2 rounded-full"
                  style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  <FaTimes size={11} /> REFUSER
                </button>
              </div>
            ) : friendshipStatus === 'pending' ? (
              <span className="flex items-center gap-2 text-sm text-white/40 px-4 py-2 rounded-full"
                style={{ background: 'rgba(255,255,255,0.05)', fontFamily: "'Rajdhani', sans-serif" }}>
                <FaUserPlus size={12} /> DEMANDE ENVOYÉE
              </span>
            ) : (
              <button
                onClick={handleAddThisUser}
                disabled={sendingFriendReq}
                className="flex items-center gap-2 text-sm font-black px-5 py-2.5 rounded-full transition-opacity disabled:opacity-50"
                style={{
                  background: 'linear-gradient(135deg, #f0b429, #c97d08)',
                  color: '#0d2b1a',
                  fontFamily: "'Rajdhani', sans-serif",
                }}
              >
                <FaUserPlus size={13} />
                {sendingFriendReq ? 'ENVOI...' : 'AJOUTER'}
              </button>
            )}
          </div>
        )}

        {/* Barre XP */}
        <div className="w-full max-w-xs">
          <div className="flex justify-between text-xs text-white/40 mb-1">
            <span>Niveau {niveau}</span>
            <span>{xpInLevel}/{xpNeeded} XP</span>
          </div>
          <div className="progress-bar h-2">
            <motion.div
              className="progress-fill h-full"
              initial={{ width: 0 }}
              animate={{ width: `${isLegacyLevel ? 100 : xpPercent}%` }}
              transition={{ duration: 1, delay: 0.3 }}
            />
          </div>
          <p className="text-white/30 text-xs text-center mt-1">
            {niveau >= LEVEL_CAP
              ? '🏆 Niveau Maximum Atteint'
              : isLegacyLevel
                ? 'Niveau hérité de l\'ancienne progression — continue à jouer !'
                : `${xpNeeded - xpInLevel} XP pour niveau ${niveau + 1}`}
          </p>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="px-4 mb-5">
        <h3 className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          <FaChartBar className="inline mr-2" />
          STATISTIQUES
        </h3>

        {/* Stats générales */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          {[
            { label: 'Parties',      value: stats.total_games },
            { label: 'Moy. score',   value: stats.avg_score   },
            { label: 'Meilleur',     value: stats.best_score  },
          ].map(({ label, value }) => (
            <div key={label} className="card text-center">
              <div className="text-2xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {value}
              </div>
              <div className="text-white/50 text-xs mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        {/* Stats Solo */}
        {(stats.solo_wins + stats.solo_losses) > 0 && (
          <div
            className="rounded-2xl p-3 border mb-3"
            style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)' }}
          >
            <p className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-1.5" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              🎮 SOLO
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Parties',   value: stats.solo_wins + stats.solo_losses, color: 'text-white'     },
                { label: 'Victoires', value: stats.solo_wins,                     color: 'text-qfl-green' },
                { label: 'Défaites',  value: stats.solo_losses,                   color: 'text-qfl-red'   },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center">
                  <div className={`text-xl font-black ${color}`} style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    {value}
                  </div>
                  <div className="text-white/40 text-xs">{label}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-white/10">
              <div
                className="h-full rounded-full bg-blue-400 transition-all"
                style={{ width: `${Math.round((stats.solo_wins / (stats.solo_wins + stats.solo_losses)) * 100)}%` }}
              />
            </div>
            <p className="text-white/30 text-xs text-center mt-1">
              {Math.round((stats.solo_wins / (stats.solo_wins + stats.solo_losses)) * 100)}% de victoires
            </p>
          </div>
        )}

        {/* Stats Duel */}
        {stats.duel_games > 0 && (
          <div
            className="rounded-2xl p-3 border"
            style={{ background: 'rgba(240,180,41,0.06)', border: '1px solid rgba(240,180,41,0.2)' }}
          >
            <p className="text-qfl-gold text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-1.5" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              ⚔️ DUEL
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Duels',     value: stats.duel_games,  color: 'text-white'        },
                { label: 'Victoires', value: stats.duel_wins,   color: 'text-qfl-green'    },
                { label: 'Défaites',  value: stats.duel_losses, color: 'text-qfl-red'      },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center">
                  <div className={`text-xl font-black ${color}`} style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    {value}
                  </div>
                  <div className="text-white/40 text-xs">{label}</div>
                </div>
              ))}
            </div>
            {stats.duel_games > 0 && (
              <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-white/10">
                <div
                  className="h-full rounded-full bg-qfl-green transition-all"
                  style={{ width: `${Math.round((stats.duel_wins / stats.duel_games) * 100)}%` }}
                />
              </div>
            )}
            <p className="text-white/30 text-xs text-center mt-1">
              {stats.duel_games > 0 ? `${Math.round((stats.duel_wins / stats.duel_games) * 100)}% de victoires` : ''}
            </p>
          </div>
        )}
      </div>

      {/* Vitrine Écussons & Trophées */}
      <div className="px-4 mb-5">
        <h3
          className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          <FaTrophy className="inline mr-2" />
          ÉCUSSONS &amp; TROPHÉES
          {profileBadges.length > 0 && (
            <span className="ml-2 text-xs text-white/40 normal-case tracking-normal font-normal">
              ({profileBadges.length})
            </span>
          )}
        </h3>

        {profileBadges.length > 0 ? (
          <div className="flex gap-3 flex-wrap">
            {profileBadges.map((badge) => {
              const meta = BADGE_META[badge.nom] || {};
              const { Icon, color, bg, border } = meta;
              return (
                <motion.div
                  key={badge.id}
                  whileTap={{ scale: 0.92 }}
                  whileHover={{ scale: 1.05 }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-2xl border cursor-default"
                  style={{
                    background:   bg   || 'rgba(255,255,255,0.06)',
                    borderColor:  border || 'rgba(255,255,255,0.15)',
                    minWidth:     '64px',
                    boxShadow:    border ? `0 0 10px ${border}` : 'none',
                  }}
                  title={badge.description || badge.nom}
                >
                  {/* Icône dans un cercle */}
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: border ? `${border}30` : 'rgba(255,255,255,0.08)' }}
                  >
                    {Icon
                      ? <Icon size={22} style={{ color: color || '#f0b429' }} />
                      : <FaStar size={22} style={{ color: '#f0b429' }} />
                    }
                  </div>
                  <span
                    className="text-[10px] font-bold text-center leading-tight"
                    style={{
                      color:       color || 'rgba(255,255,255,0.7)',
                      fontFamily:  "'Rajdhani', sans-serif",
                      maxWidth:    '60px',
                    }}
                  >
                    {badge.nom}
                  </span>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <FaTrophy size={28} className="mx-auto mb-2 text-white/10" />
            <p className="text-white/30 text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Aucun écusson pour l'instant.
            </p>
            <p className="text-white/20 text-xs mt-1">
              Joue des parties, remporte des duels et des championnats !
            </p>
          </div>
        )}
      </div>

      {/* Demandes d'ami en attente */}
      {isOwnProfile && pendingRequests.length > 0 && (
        <div className="px-4 mb-5">
          <h3 className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            <FaUserPlus className="inline mr-2" />
            DEMANDES D'AMI ({pendingRequests.length})
          </h3>
          <div className="flex flex-col gap-2">
            {pendingRequests.map((req) => (
              <div key={req.friendship_id} className="flex items-center gap-3 card py-3">
                <Avatar user={{ pseudo: req.pseudo, avatar_url: req.avatar_url }} size="sm" />
                <div className="flex-1">
                  <p className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{req.pseudo}</p>
                  <p className="text-white/40 text-xs">Niveau {req.level}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAcceptFriend(req.user_id)}
                    className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center hover:bg-green-500/40 transition-colors"
                  >
                    <FaCheck size={12} />
                  </button>
                  <button
                    onClick={() => handleRejectFriend(req.user_id)}
                    className="w-8 h-8 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/40 transition-colors"
                  >
                    <FaTimes size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reconnaissance faciale — profil personnel uniquement */}
      {isOwnProfile && <FingerprintSection />}
      {isOwnProfile && <BiometricSection />}

      {/* Parrainage */}
      {isOwnProfile && authUser?.plan === 'premium' && referral && (
        <ReferralSection referral={referral} onWithdraw={async (form) => {
          try {
            const { data } = await api.post('/referral/withdraw', form);
            toast.success(data.message || 'Demande envoyée !');
            const res = await api.get('/referral/me');
            setReferral(res.data);
          } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur lors de la demande');
          }
        }} />
      )}
      {isOwnProfile && authUser?.plan !== 'premium' && (
        <div className="px-4 mb-5">
          <h3 className="text-white/40 font-bold uppercase tracking-widest text-sm mb-3"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            <FaGift className="inline mr-2" />
            PARRAINAGE
          </h3>
          <div
            className="rounded-2xl p-5 flex flex-col items-center gap-3 text-center"
            style={{ background: 'rgba(240,180,41,0.05)', border: '1px dashed rgba(240,180,41,0.25)' }}
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(240,180,41,0.12)' }}>
              <FaGift size={20} style={{ color: '#f0b429' }} />
            </div>
            <div>
              <p className="text-white font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Réservé aux membres Premium
              </p>
              <p className="text-white/40 text-xs mt-1">
                Passe en Premium pour parrainer des joueurs et gagner des FCFA à chaque abonnement de tes filleuls.
              </p>
            </div>
            <button
              onClick={() => navigate('/shop')}
              className="px-5 py-2 rounded-xl font-black text-sm uppercase"
              style={{
                background: 'linear-gradient(135deg, #f0b429, #fbbf24)',
                color: '#000',
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              PASSER PREMIUM
            </button>
          </div>
        </div>
      )}

      {/* Ajouter un ami par pseudo */}
      {isOwnProfile && (
        <div className="px-4 mb-5">
          <h3 className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            <FaUserPlus className="inline mr-2" />
            AJOUTER UN AMI
          </h3>
          <form onSubmit={handleSendFriendRequest} className="flex gap-2">
            <input
              type="text"
              placeholder="Pseudo de l'ami..."
              value={addFriendPseudo}
              onChange={(e) => setAddFriendPseudo(e.target.value)}
              className="form-input flex-1 text-sm"
              maxLength={20}
            />
            <button
              type="submit"
              disabled={sendingFriendReq || !addFriendPseudo.trim()}
              className="px-4 py-2 rounded-xl font-black text-sm uppercase disabled:opacity-40 transition-opacity flex items-center gap-1.5"
              style={{
                background: 'linear-gradient(135deg, #f0b429, #c97d08)',
                color: '#0d2b1a',
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              <FaSearch size={11} />
              {sendingFriendReq ? '...' : 'ENVOYER'}
            </button>
          </form>
        </div>
      )}

      {/* Liste des amis */}
      {isOwnProfile && (
        <div className="px-4 mb-5">
          <h3 className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            <FaUserFriends className="inline mr-2" />
            MES AMIS ({friends.length})
          </h3>
          {friends.length === 0 ? (
            <p className="text-white/30 text-sm text-center py-4">Aucun ami pour l'instant</p>
          ) : (
            <div className="flex flex-col gap-2">
              {friends.map((friend) => (
                <div
                  key={friend.id}
                  className="flex items-center gap-3 card py-3"
                >
                  <div
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => navigate(`/profile/${friend.id}`)}
                  >
                    <Avatar user={{ pseudo: friend.pseudo, avatar_url: friend.avatar_url }} size="sm" />
                    <div className="flex-1">
                      <p className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{friend.pseudo}</p>
                      <p className="text-white/40 text-xs">Niveau {friend.level}</p>
                    </div>
                  </div>
                  <button
                    disabled={challenging === friend.id}
                    onClick={async () => {
                      setChallenging(friend.id);
                      try {
                        const { data } = await api.post(`/profile/friends/challenge/${friend.id}`);
                        toast.success(`Défi envoyé à ${friend.pseudo} !`);
                        navigate(`/duel?gameId=${data.gameId}`);
                      } catch (err) {
                        toast.error(err?.response?.data?.message || 'Erreur');
                      } finally {
                        setChallenging(null);
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-40"
                    style={{
                      background: 'rgba(240,180,41,0.12)',
                      border: '1px solid rgba(240,180,41,0.3)',
                      color: '#f0b429',
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                    title={`Défier ${friend.pseudo}`}
                  >
                    {challenging === friend.id ? (
                      <div className="w-3 h-3 border-2 border-qfl-gold border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FaCrosshairs size={11} />
                    )}
                    DÉFIER
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Historique */}
      <div className="px-4 mb-5">
        <h3 className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          <FaHistory className="inline mr-2" />
          PARTIES RÉCENTES
        </h3>
        <div className="flex flex-col gap-2">
          {displayHistory.map((game) => (
            <div key={game._id} className="flex items-center gap-3 card py-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  game.won || game.correct >= game.total / 2
                    ? 'bg-qfl-green/20 text-qfl-green'
                    : 'bg-qfl-red/20 text-qfl-red'
                }`}
              >
                {game.mode === 'Duel' ? <FaFutbol size={16} /> : <FaStar size={16} />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    {game.mode || 'Solo'}
                  </span>
                  {game.won && (
                    <span className="text-xs bg-qfl-green/20 text-qfl-green px-2 py-0.5 rounded-full">Victoire</span>
                  )}
                </div>
                <span className="text-white/40 text-xs">
                  {game.correct}/{game.total} correctes
                </span>
              </div>
              <div className="text-right">
                <div className="font-black text-qfl-gold text-lg" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  +{game.score}
                </div>
                <div className="text-white/30 text-xs">pts</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notifications push */}
      {isOwnProfile && pushSupported && (
        <div className="px-4 mb-2">
          <button
            onClick={async () => {
              if (subscribed) { await unsubscribePush(); toast('Notifications désactivées', { icon: '🔕' }); }
              else { const ok = await subscribePush(); if (ok) toast.success('Notifications activées !'); else toast.error('Permission refusée'); }
            }}
            className="w-full py-3 rounded-2xl flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wider transition-colors"
            style={{
              background: subscribed ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.05)',
              border: subscribed ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(255,255,255,0.1)',
              fontFamily: "'Rajdhani', sans-serif",
              color: subscribed ? '#22c55e' : 'rgba(255,255,255,0.5)',
            }}
          >
            {subscribed ? <FaBell size={13} /> : <FaBellSlash size={13} />}
            {subscribed ? 'NOTIFICATIONS ACTIVÉES' : 'ACTIVER LES NOTIFICATIONS'}
          </button>
        </div>
      )}

      {/* Changer le mot de passe */}
      {isOwnProfile && (
        <div className="px-4 mb-4">
          {!showPasswordForm ? (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="w-full py-3 rounded-2xl flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wider transition-colors"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                fontFamily: "'Rajdhani', sans-serif",
                color: 'rgba(255,255,255,0.5)',
              }}
            >
              <FaKey size={13} />
              CHANGER MON MOT DE PASSE
            </button>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleChangePassword}
              className="rounded-2xl p-4 flex flex-col gap-3"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/70 font-bold text-sm flex items-center gap-2"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  <FaKey size={12} /> MOT DE PASSE
                </span>
                <button type="button" onClick={() => { setShowPasswordForm(false); setPwForm({ current: '', next: '', confirm: '' }); }}
                  className="text-white/30 hover:text-white/60"><FaTimes size={14} /></button>
              </div>
              <input
                type="password"
                placeholder="Mot de passe actuel"
                value={pwForm.current}
                onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
                className="form-input w-full text-sm"
                required
              />
              <input
                type="password"
                placeholder="Nouveau mot de passe (min. 8 caractères)"
                value={pwForm.next}
                onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
                className="form-input w-full text-sm"
                required
              />
              <input
                type="password"
                placeholder="Confirmer le nouveau mot de passe"
                value={pwForm.confirm}
                onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                className="form-input w-full text-sm"
                required
              />
              <button
                type="submit"
                disabled={savingPw}
                className="w-full py-2.5 rounded-xl font-black text-sm uppercase tracking-widest disabled:opacity-50 transition-opacity"
                style={{
                  background: 'linear-gradient(135deg, #f0b429, #c97d08)',
                  color: '#0d2b1a',
                  fontFamily: "'Rajdhani', sans-serif",
                }}
              >
                {savingPw ? 'Enregistrement...' : 'CONFIRMER'}
              </button>
            </motion.form>
          )}
        </div>
      )}

      {/* Déconnexion + suppression de compte */}
      {isOwnProfile && (
        <div className="px-4 pb-4 flex flex-col gap-3">
          <Button variant="outline" size="md" fullWidth onClick={() => navigate('/guide')}>
            <FaQuestionCircle size={16} />
            GUIDE / AIDE
          </Button>
          <Button variant="danger" size="md" fullWidth onClick={handleLogout}>
            <FaSignOutAlt size={16} />
            SE DÉCONNECTER
          </Button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="text-xs text-white/25 hover:text-red-400 transition-colors text-center py-1"
          >
            Supprimer mon compte
          </button>
          <p className="text-xs text-white/50 text-center font-semibold">Version {BUILD_ID}</p>
        </div>
      )}

      {/* Modal confirmation suppression */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setShowDeleteModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-sm rounded-2xl p-6"
            style={{ background: '#0d2b1a', border: '1px solid rgba(239,68,68,0.3)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-lg font-bold text-red-400 mb-2 uppercase tracking-widest"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              Supprimer le compte
            </h3>
            <p className="text-white/60 text-sm mb-4 leading-relaxed">
              Cette action est <strong className="text-white">irréversible</strong>. Toutes tes données (stats, pièces, historique) seront définitivement effacées.
            </p>
            <input
              type="password"
              placeholder="Confirme ton mot de passe"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="form-input mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2 rounded-xl text-sm font-bold text-white/60"
                style={{ background: 'rgba(255,255,255,0.08)' }}
              >
                Annuler
              </button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteAccount}
                loading={deletingAccount}
                className="flex-1"
              >
                SUPPRIMER
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
    </PageTransition>
  );
}

export default Profile;
